/**
 * Trend/Record Visualization — served from the live capture in
 * trendViz.generated.ts (178 concepts, 1987→2024, pulled at window 'all').
 *
 * This module holds NO clinical content of its own. Its whole job is to answer
 * each request under the history window the caller asked for, by narrowing the
 * captured record — the same narrowing the backend does, where the effective
 * window is the narrower of the session's and the request's.
 *
 * An earlier version hand-built a two-series inventory from the blood-pressure
 * answer's tables. It plotted 86 points where the chart holds 1,100+ records,
 * so the Trend page showed a fraction of the patient it claimed to show.
 */

import type {
  ConditionRange, CourseRange, InventoryCategory, InventoryItem,
  NumericSeries, TimelineEvent, TrendAlert,
} from '../../components/trend-viz/types';
import { DEMO_HISTORY_WINDOW, DEMO_TODAY } from './patient';
import {
  TREND_ALERTS, TREND_CATEGORIES, TREND_CONDITION_RANGES, TREND_COURSE_RANGES,
  TREND_EVENTS, TREND_NUMERIC_SERIES,
} from './trendViz.generated';

/** The five history windows, matching SmartHistoryWindow. */
const WINDOW_YEARS: Record<string, number | null> = {
  '1y': 1, '2y': 2, '5y': 5, '10y': 10, all: null,
};
const WINDOW_LABELS: Record<string, string> = {
  '1y': 'Last 1 year', '2y': 'Last 2 years', '5y': 'Last 5 years',
  '10y': 'Last 10 years', all: 'All data',
};

/**
 * The demo is windowed for real rather than always answering with everything:
 * the launch picker's choice reaches these endpoints, and a chart that ignored
 * it would quietly contradict the window badge the header displays. An
 * unrecognised code falls back to the window this demo actually launched with,
 * never to a narrower one — that fallback was itself the bug that clipped the
 * whole record to two years.
 */
export function resolveWindow(code: unknown) {
  const key = typeof code === 'string' && code in WINDOW_YEARS ? code : DEMO_HISTORY_WINDOW;
  const years = WINDOW_YEARS[key];
  const start =
    years == null
      ? null
      : new Date(Date.UTC(DEMO_TODAY.getUTCFullYear() - years, DEMO_TODAY.getUTCMonth(), DEMO_TODAY.getUTCDate()))
          .toISOString()
          .slice(0, 10);
  return { window: { code: key, label: WINDOW_LABELS[key], years }, start };
}

/** The capture mixes ISO 'T' and space separators, and some values carry an
 *  offset. Only the calendar day matters for windowing, so compare on that. */
function day(value: string | null | undefined): string | null {
  return value ? String(value).slice(0, 10) : null;
}

/** True when a dated record falls inside the window (null start = all history). */
function inWindow(date: string | null | undefined, start: string | null): boolean {
  const d = day(date);
  if (!d) return false;
  return start == null || d >= start;
}

/** Every date behind one concept, so a windowed count is counted rather than
 *  taken from the capture's full-history total. */
function datesById(): Map<string, string[]> {
  const map = new Map<string, string[]>();
  const add = (id: string, dates: Array<string | null | undefined>) => {
    map.set(id, dates.map(day).filter((d): d is string => !!d).sort());
  };
  for (const s of TREND_NUMERIC_SERIES) add(s.id, s.points.map((p) => p.date));
  for (const e of TREND_EVENTS) add(e.id, e.occurrences.map((o) => o.date));
  for (const c of TREND_COURSE_RANGES) {
    add(c.id, (c.order_starts ?? (c.orders ?? []).map((o) => o.date)) as Array<string | null>);
  }
  for (const c of TREND_CONDITION_RANGES) add(c.id, [c.onset]);
  return map;
}
const DATES = datesById();

/** A problem that is still active overlaps every window, however old its
 *  onset — closing it at the window edge would report a resolved condition. */
function conditionInWindow(c: ConditionRange, start: string | null): boolean {
  if (start == null || c.active) return true;
  return inWindow(c.onset, start) || inWindow(c.end, start);
}

export function demoTrendInventory(windowCode?: unknown) {
  const { window, start } = resolveWindow(windowCode);
  const conditionIds = new Set(
    TREND_CONDITION_RANGES.filter((c) => conditionInWindow(c, start)).map((c) => c.id),
  );

  const categories: InventoryCategory[] = [];
  for (const category of TREND_CATEGORIES) {
    const items: InventoryItem[] = [];
    for (const item of category.items) {
      if (item.plot_class === 'condition-range') {
        if (conditionIds.has(item.id)) items.push(item);
        continue;
      }
      const dates = (DATES.get(item.id) ?? []).filter((d) => inWindow(d, start));
      if (!dates.length) continue;
      items.push({
        ...item,
        count: dates.length,
        date_span: { first: dates[0], last: dates[dates.length - 1] },
      });
    }
    if (!items.length) continue;
    categories.push({
      ...category,
      count: items.length,
      record_count: items.reduce((n, i) => n + (i.count || 0), 0),
      items,
    });
  }

  return { window, categories };
}

export function demoTrendSeries(itemIds: string[], windowCode?: unknown) {
  const { window, start } = resolveWindow(windowCode);
  const wanted = new Set(itemIds || []);

  const numeric_series: NumericSeries[] = TREND_NUMERIC_SERIES
    .filter((s) => wanted.has(s.id))
    .map((s) => ({ ...s, points: s.points.filter((p) => inWindow(p.date, start)) }));

  const condition_ranges: ConditionRange[] = TREND_CONDITION_RANGES
    .filter((c) => wanted.has(c.id) && conditionInWindow(c, start))
    // An onset before the window edge is marked clipped, so the ribbon reads as
    // running off the left of the chart rather than starting at the edge.
    .map((c) => ({ ...c, clipped_left: c.clipped_left || (start != null && !inWindow(c.onset, start)) }));

  const events: TimelineEvent[] = TREND_EVENTS
    .filter((e) => wanted.has(e.id))
    .map((e) => {
      const occurrences = e.occurrences.filter((o) => inWindow(o.date, start));
      return { ...e, occurrences, count: occurrences.length };
    });

  const course_ranges: CourseRange[] = TREND_COURSE_RANGES
    .filter((c) => wanted.has(c.id))
    .map((c) => {
      const orders = (c.orders ?? []).filter((o) => inWindow(o.date, start));
      const order_starts = (c.order_starts ?? []).filter((d) => inWindow(d, start));
      return {
        ...c,
        orders,
        order_starts,
        order_count: order_starts.length || orders.length || c.order_count,
      };
    });

  return { window, numeric_series, condition_ranges, events, course_ranges };
}

export function demoTrendAlerts(windowCode?: unknown) {
  const { window, start } = resolveWindow(windowCode);
  // A detection is reported in the window it was detected in. Its supporting
  // points are left whole: a comparison against an earlier baseline is what
  // the sentence describes, and trimming them would leave the text unsupported.
  const alerts: TrendAlert[] = TREND_ALERTS.filter((a) => start == null || inWindow(a.detected_date, start));
  return { window, alerts, count: alerts.length };
}
